"use client";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export type CronCalendarProps = {
  // Accepts "L" as well as dates so a saved last-day-of-month schedule round-trips back into the
  // checkbox instead of arriving as an unchecked box over a cron that still says "L".
  selectedDays?: (number | "L")[];
  onChange?: (days: (number | "L")[]) => void;
  disabled?: boolean;
};

// Mirrors CronInput's DEFAULT_MONTH_DAY. Not imported, to keep this ui/ component independent of the
// custom/ one that consumes it.
const FALLBACK_DAY = 1;

// While "L" is active, this cell is highlighted as a visual anchor for it - 31 being the furthest a
// month can run. It is a *marker only*: it is never part of the selection, so it renders in the
// warning colour rather than the selected one, and never reaches the cron (which carries "L").
const LAST_DAY_MARKER_DATE = 31;

// Dates that "L" subsumes. Picking "L" drops them: it already means "whichever of these is the last
// day of this particular month", so keeping the specific date it replaced adds nothing - and any such
// memory would be session-only regardless, since the cron has nowhere to record it.
const DATES_SUBSUMED_BY_LAST_DAY = [29, 30, 31];

function CronCalendar({ selectedDays = [], onChange }: CronCalendarProps) {
  const [days, setDays] = useState<(number | "L")[]>(selectedDays);
  const { t } = useTranslation();

  // Derived rather than held as separate state, which used to drift: toggleDay strips "L" from the
  // selection for *any* newly picked date but only unchecked the box for 29/30/31, so picking the
  // 5th left the checkbox ticked over a cron that no longer said "L".
  const lastDaySelected = days.includes("L");

  // Notified from the interaction handlers rather than from an effect on [days, onChange].
  // That effect fired on mount - reporting a selection change the user never made - and again on
  // every parent render, because consumers pass an inline arrow whose identity changes each time.
  // Against a parent that saves on change (the metric-alert detail panel) that became a feedback
  // loop: notify -> parent setState -> parent re-render -> new onChange identity -> notify again,
  // one PATCH per cycle, which is what made the calendar flicker.
  const commit = (updatedDays: (number | "L")[]) => {
    setDays(updatedDays);
    onChange?.(updatedDays);
  };

  const clearLastDay = () => {
    const remaining = days.filter((d) => d !== "L");
    // The selection must not go empty: a monthly cron with no date has nothing to fire on, and the
    // consumer would substitute a default this calendar isn't displaying.
    commit(remaining.length > 0 ? remaining : [FALLBACK_DAY]);
  };

  const toggleDay = (day: number) => {
    // The marker cell is not a selected date, so clicking it clears "L" rather than selecting the
    // 31st - which is what the highlight invites. Both the highlight and the checkbox clear together,
    // since the checkbox is derived from the same selection.
    if (lastDaySelected && day === LAST_DAY_MARKER_DATE) {
      clearLastDay();
      return;
    }

    const isDaySelected = days.includes(day);

    // A monthly schedule needs at least one date, so the last remaining one cannot be deselected.
    if (isDaySelected && days.length === 1) return;

    // Picking any date clears "L": the two are mutually exclusive here.
    const updatedDays = isDaySelected
      ? days.filter((d) => d !== day)
      : [...days.filter((d) => d !== "L"), day];

    commit(updatedDays);
  };

  const handleLastDayToggle = () => {
    if (lastDaySelected) {
      clearLastDay();
      return;
    }
    commit([
      "L",
      ...days.filter(
        (d) => typeof d !== "number" || !DATES_SUBSUMED_BY_LAST_DAY.includes(d),
      ),
    ]);
  };

  return (
    <div>
      <div
        className={cn("grid grid-cols-7 p-4 gap-2 border rounded-md w-auto")}
      >
        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
          <button
            type="button"
            key={day}
            className={cn(
              "w-10 h-10 rounded-md flex items-center justify-center",
              days.includes(day)
                ? "bg-azure text-white"
                : lastDaySelected && day === LAST_DAY_MARKER_DATE
                  ? "bg-warning text-foreground"
                  : "text-foreground hover:bg-sky",
            )}
            onClick={() => toggleDay(day)}
          >
            {day}
          </button>
        ))}
      </div>
      {/* Shown once "L" is active as well as when one of the dates it subsumes is picked: otherwise a
          schedule loaded as "last day of the month" hid the very checkbox describing it. */}
      {(lastDaySelected ||
        DATES_SUBSUMED_BY_LAST_DAY.some((day) => days.includes(day))) && (
        <div className="mt-4 flex items-center">
          <Checkbox
            id="lastDay"
            checked={lastDaySelected}
            onCheckedChange={handleLastDayToggle}
            aria-label={t("setAsLastDay")}
          />
          <label className="ml-2">{t("setAsLastDay")}</label>
        </div>
      )}
    </div>
  );
}

CronCalendar.displayName = "CronCalendar";

export { CronCalendar };
